import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ListParams } from '@/types';

// ─── Tipos ────────────────────────────────────────────────────
interface UIStore {
  // Sidebar
  sidebarAbierto: boolean;
  setSidebarAbierto: (open: boolean) => void;
  toggleSidebar: () => void;

  // Modales — solo un modal activo a la vez
  modalActivo: string | null;
  setModalActivo: (modal: string | null) => void;
  cerrarModal: () => void;

  // Filtros globales compartidos entre vistas
  filtrosGlobales: ListParams;
  setFiltrosGlobales: (filtros: Partial<ListParams>) => void;
  resetFiltros: () => void;

  // Badge notificaciones (actualizado por WS badge_count)
  noLeidas: number;
  setNoLeidas: (n: number) => void;
  incrementNoLeidas: () => void;

  // Asistente IA
  aiAssistantOpen: boolean;
  setAIAssistantOpen: (open: boolean) => void;
  toggleAIAssistant: () => void;
}

// ─── Valores por defecto ──────────────────────────────────────
const FILTROS_DEFECTO: ListParams = {
  page: 1,
  per_page: 25,
  sort: 'creado_en',
  order: 'desc',
};

// ─── Store — SOLO estado de UI (no estado de servidor) ────────
// Para estado de servidor usar TanStack Query (hooks/use*.ts)
// sidebarAbierto persiste en localStorage para recordar preferencia del usuario
export const useUIStore = create<UIStore>()(
  persist(
    (set) => ({
      // Sidebar
      sidebarAbierto: true,
      setSidebarAbierto: (open) => set({ sidebarAbierto: open }),
      toggleSidebar: () =>
        set((state) => ({ sidebarAbierto: !state.sidebarAbierto })),

      // Modal
      modalActivo: null,
      setModalActivo: (modal) => set({ modalActivo: modal }),
      cerrarModal: () => set({ modalActivo: null }),

      // Filtros
      filtrosGlobales: { ...FILTROS_DEFECTO },
      setFiltrosGlobales: (filtros) =>
        set((state) => ({
          filtrosGlobales: {
            ...state.filtrosGlobales,
            ...filtros,
            page: 1,
          },
        })),
      resetFiltros: () => set({ filtrosGlobales: { ...FILTROS_DEFECTO } }),

      // Badge notificaciones
      noLeidas: 0,
      setNoLeidas: (n) => set({ noLeidas: n }),
      incrementNoLeidas: () => set((state) => ({ noLeidas: state.noLeidas + 1 })),

      // Asistente IA
      aiAssistantOpen: false,
      setAIAssistantOpen: (open) => set({ aiAssistantOpen: open }),
      toggleAIAssistant: () => set((state) => ({ aiAssistantOpen: !state.aiAssistantOpen })),
    }),
    {
      name: 'erp-ui',
      partialize: (state: UIStore) => ({ sidebarAbierto: state.sidebarAbierto }),
    },
  ),
);
